import partition from 'lodash/partition';
import findIndex from 'lodash/findIndex';
import without from 'lodash/without';
import sortBy from 'lodash/sortBy';
import reject from 'lodash/reject';
import { GRIDSNAP, TYPE, TABLE, SEATSTATUS, TEXT } from '@/constants';
import { ref } from 'vue';

export function adjustBBox(x, y, x2, y2) {
  const bbox = { x, y, x2, y2 };

  if (x2 <= x && y2 >= y) {
    bbox.x = x2;
    bbox.x2 = x;
  } else if (y2 <= y && x2 >= x) {
    bbox.y = y2;
    bbox.y2 = y;
  } else if (y2 < y && x2 < x) {
    bbox.x = x2;
    bbox.x2 = x;
    bbox.y = y2;
    bbox.y2 = y;
  }
  bbox.width = bbox.x2 - bbox.x;
  bbox.height = bbox.y2 - bbox.y;

  return bbox;
}

/**
 * Allows to get coordinates on Quadratic Bezier curve
 * @param {number} startPos Coordinate of start point
 * @param {number} controlPos Coordinate of control point
 * @param {number} endPos Coordinate of end point
 * @param {number} t The Bezier interval, 0 <= t <= 1
 */
export function weightedBasisFunction(startPos, controlPos, endPos, t) {
  return startPos * (1 - t) * (1 - t) + controlPos * (2 * (1 - t) * t) + endPos * t * t;
}

export function sortSeatsByPositionInRow(seatA, seatB) {
  return seatA.pos - seatB.pos;
}

export function sortSeatsByX(a, b) {
  return a.rbox().cx - b.rbox().cx;
}

export function sortUnitsByDOMPosition(unitA, unitB) {
  return unitA.view.position() - unitB.view.position();
}

export function calculateSkewGaps(seatsCount, skewStep) {
  let gaps;
  const rightGaps = [];
  const isEven = seatsCount % 2 === 0;
  const halfCount = seatsCount / 2;
  if (isEven) {
    for (let i = 0; i < halfCount; i++) {
      rightGaps.push(i * skewStep + skewStep / 2);
    }
  } else {
    for (let i = 1; i < halfCount; i++) {
      rightGaps.push(i * skewStep);
    }
  }
  const leftGaps = rightGaps.map((g) => -g).reverse();
  if (isEven) {
    gaps = [...leftGaps, ...rightGaps];
  } else {
    gaps = [...leftGaps, 0, ...rightGaps];
  }
  return gaps;
}

/**
 * Returns true if event button is left
 * @param event
 * @returns {boolean}
 */
export function detectLeftButton(event) {
  if (event.buttons) {
    return event.buttons === 1;
  }
  return event.which ? event.which === 1 : event.button === 1;
}

/**
 * Returns normalized indexes for two side rect table
 * @param {*} table Table
 */
function normalizeTwoSideRectTableIndexes(table) {
  table.seats.forEach((s, i) => {
    s.attributes.index = i;
  });
  return table;
}

/**
 * Returns normalized indexes for four side rect table and sorts seats by new indexes
 * @param table Table
 */
function normalizeFourSideRectTableIndexes(table) {
  const [odd, even] = partition(table.seats, (s) => s.attributes.orderid % 2 === 1);
  even.forEach((s, i) => {
    s.attributes.index = i;
  });
  [odd[0], ...odd.slice(1).reverse()]
    .filter((s) => s)
    .forEach((s, i) => {
      s.attributes.index = i + even.length;
    });
  table.seats = sortBy(table.seats, (s) => s.attributes.index);
  return table;
}

/**
 * Returns copy of rowItem with recalculated indexes
 * @param {*} rowItem Row or table
 */
function recalculateSeatIndexes(rowItem) {
  const { ONESIDE, FOURSIDES } = TABLE.RECTANGULAR.DIRECTION;
  //let ri = cloneDeep(rowItem);
  rowItem.seats = rowItem.seats.filter((s) => !s.locked); // TODO add unavailable
  let indexesRecalculated;
  if (rowItem.type === TYPE.TABLE) {
    rowItem.seats = sortBy(rowItem.seats, (s) => s.attributes.orderid);
    if (rowItem.subtype === TYPE.TABLE_RECTANGULAR && rowItem.seatsDirection !== ONESIDE) {
      if (rowItem.seatsDirection === FOURSIDES) {
        rowItem = normalizeFourSideRectTableIndexes(rowItem);
      } else {
        rowItem = normalizeTwoSideRectTableIndexes(rowItem);
      }
      indexesRecalculated = true;
    }
  } else {
    rowItem.seats = sortBy(rowItem.seats, (s) => s.attributes.pos);
  }
  if (!indexesRecalculated) {
    rowItem.seats.forEach((s, i) => {
      s.attributes.index = i;
    });
  }
  return rowItem;
}

/**
 * Checks circle edges and combine them
 */
function handleCircleEdgesGap(gaps, seatsLength) {
  const firstGap = gaps[0];
  const lastGap = gaps[gaps.length - 1];
  if (firstGap.start === 0 && lastGap.start + lastGap.length === seatsLength) {
    gaps = without(gaps, firstGap);
    lastGap.length += firstGap.length;
  }
  return gaps;
}

/**
 * Inverse seat state and search for gap
 * @param rowSeats
 * @param seatId
 * @param gapSize
 * @param round
 * @returns {Boolean} What will be if current seat will be inversed(Gap or not a gap)
 */
function checkOnFutureGap(rowSeats, seatId, categoryId, gapSize, round, tableSeat) {
  rowSeats = reject(
    rowSeats,
    (s) =>
      s.attributes.status === SEATSTATUS.OCCUPIED ||
      s.attributes.status === SEATSTATUS.RESERVED ||
      s.attributes.status === SEATSTATUS.CART ||
      s.attributes.status === SEATSTATUS.DISABLED ||
      s.attributes.invisible === true ||
      s.attributes.categoryId != categoryId
  );
  let seatIndex = findIndex(rowSeats, (s) => s.uuid === seatId);
  let actualSeats = [];
  if (!tableSeat) {
    const seatsTotal = rowSeats.length;
    let startIndex = 0;
    let endIndex = seatsTotal - 1;
    rowSeats.forEach((s, j) => {
      if (j < endIndex) {
        const nextSeat = rowSeats[j + 1];
        const xDistance = nextSeat.attributes.position[0] - s.attributes.position[0];
        const yDistance = nextSeat.attributes.position[1] - s.attributes.position[1];
        const distance = Math.sqrt(xDistance ** 2 + yDistance ** 2);
        const maxDistanceBetweenConsecutiveSeats = GRIDSNAP * (gapSize + 1) - 0.01;
        if (distance > maxDistanceBetweenConsecutiveSeats && j < seatIndex) {
          startIndex = j + 1;
        }
        if (distance > maxDistanceBetweenConsecutiveSeats && j > seatIndex) {
          endIndex = j;
        }
      }
    });
    for (startIndex; startIndex <= endIndex; startIndex++) {
      actualSeats.push(rowSeats[startIndex]);
    }
  } else {
    actualSeats = rowSeats;
  }
  const seatsStates = actualSeats.map((s) => s.selected);
  seatIndex = findIndex(actualSeats, (s) => s.uuid === seatId);
  seatsStates[seatIndex] = !seatsStates[seatIndex];
  let currentGap = 0;
  let gaps = [];
  // if (seatsStates.every(s => s) || seatsStates.every(s => !s)) {
  //   return false;
  // }
  seatsStates.forEach((s, i) => {
    if (!s) {
      currentGap += 1;
      return;
    } else if (currentGap > 0) {
      gaps.push({
        start: i - currentGap,
        length: currentGap,
      });
    }
    currentGap = 0;
  });
  if (currentGap > 0) {
    gaps.push({
      start: seatsStates.length - currentGap,
      length: currentGap,
    });
  }
  if (round && gaps.length) {
    gaps = handleCircleEdgesGap(gaps, seatsStates.length);
  }
  if (round) {
    const badGaps = gaps.filter((g) => g.length <= gapSize);
    return gaps.length > 1 && badGaps.length;
  }
  const firstState = seatsStates[0];
  const lastState = seatsStates[seatsStates.length - 1];
  const onlyEdgeUnselected =
    (!firstState && seatsStates.slice(gapSize).every((s) => s)) ||
    (!lastState && seatsStates.slice(0, seatsStates.length - gapSize).every((s) => s));
  if (onlyEdgeUnselected) {
    return false;
  }
  return gaps.some((g) => g.length <= gapSize);
}

/**
 * Returns true if selecting this seat will be with gap
 */
export function checkSeatForGap(seat) {
  const { ONESIDE, TWOSIDES } = TABLE.RECTANGULAR.DIRECTION;
  const gapSize = 1;
  const rowSeats = recalculateSeatIndexes(seat.row || seat.table).seats;
  const selectedCat = seat.attributes.categoryId;
  if (seat.table) {
    if (seat.table.seatsDirection === ONESIDE) {
      return checkOnFutureGap(rowSeats, seat.uuid, selectedCat, gapSize, false);
    }
    if (seat.table.seatsDirection === TWOSIDES) {
      const [firstPart, secondPart] = partition(rowSeats, (s) => s.attributes.orderid % 2 === 1);
      const seatInFirstPart = firstPart.some((s) => s.uuid === seat.uuid);
      if (
        (seatInFirstPart && checkOnFutureGap(firstPart, seat.uuid, selectedCat, gapSize, false)) ||
        checkOnFutureGap(secondPart, seat.uuid, selectedCat, gapSize, false)
      ) {
        return true;
      }
      return false;
    }
    return checkOnFutureGap(rowSeats, seat.uuid, selectedCat, gapSize, true, true);
  }
  return checkOnFutureGap(rowSeats, seat.uuid, selectedCat, gapSize, false, false);
}

export function snap(value, gridSize = GRIDSNAP) {
  return gridSize * Math.round(value / gridSize);
}

// export const getIconComponent = (icon, attrs = {}) => ({
//   render(h) {
//     return h(Icon, { attrs: { icon, ...attrs } });
//   },
// });

export const saveToStorage = (key, value) => {
  window.localStorage.setItem(key, JSON.stringify(value));
};

export const getFromStorage = (key, defaultValue) => {
  const value = window.localStorage.getItem(key);
  return value ? JSON.parse(value) ?? defaultValue : defaultValue;
};

// tsdoc Ref<number>
/**
 * @type {import('vue').Ref<number>}
 */
export const lastUsedSectionFontSize = ref(TEXT.SIZE.SECTION);
